import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import '@kitware/vtk.js/Rendering/Profiles/All';
import '@kitware/vtk.js/Rendering/Misc/RenderingAPIs';

import { PubSubService, ServiceContext, ServiceRegistration } from '../core';
import { InteractionService } from '../interaction/interaction-service';
import { ToolGroupManager } from '../tools/tool-group-manager';
import { CprViewport2D } from '../viewports/cpr-viewport-2d';
import { ObliqueSliceViewport2D } from '../viewports/oblique-slice-viewport-2d';
import { OrthogonalSliceViewport2D } from '../viewports/orthogonal-slice-viewport-2d';
import { SliceViewport2D } from '../viewports/slice-viewport-2d';
import {
    AnyViewportOptions,
    IViewport,
    ViewportType,
    ViewportVtkContext,
    SnapshotOptions,
} from '../viewports/types';
import { VolumeViewport3D } from '../viewports/volume-viewport';

// ============================================================================
// Facade types
// ============================================================================

export interface SliceInfo {
    sliceIndex: number;
    sliceCount: number;
    isOblique: boolean;
}

/**
 * Standalone creation: orchestration flags plus the viewport's own creation
 * options, flattened into a single object. Options specific to the requested
 * type (e.g. `color3DPresetId` for VOLUME_3D) go here alongside the common ones.
 */
export type StandaloneViewportOptions = AnyViewportOptions & {
    viewportType: ViewportType;
    container: HTMLElement;
    /** Whether to track container resize and re-render. Default: true. */
    trackResize?: boolean;
};

export interface StandaloneViewportHandle {
    viewport: IViewport;
    dispose: () => void;
}

export class ViewportService extends PubSubService {
    static readonly SERVICE_NAME = 'ViewportService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: ViewportService.SERVICE_NAME,
        factory: (context: ServiceContext) => new ViewportService(context),
    };

    readonly EVENTS = {
        VIEWPORT_CREATED: 'viewport:created',
        VIEWPORT_DESTROYED: 'viewport:destroyed',
        /** Camera reset to default framing. Payload: { viewportId: string } */
        CAMERA_RESET: 'viewport:camera:reset',
    } as const;

    private readonly context: ServiceContext;
    private readonly mainRenderWindow: vtkRenderWindow;
    private readonly mainView;
    private readonly viewports = new Map<string, IViewport>();

    // Rendering debouncing using animation frames

    private dirtyViewports = new Set<string>(); // Ids of viewports planed for next rendering
    private pendingRenderRequestHandle: number | null = null; // Animation frame handle, null if no rendering scheduled

    constructor(context: ServiceContext) {
        super();
        this.context = context;
        // Initialize shared WebGL context
        this.mainRenderWindow = vtkRenderWindow.newInstance();
        this.mainView = this.mainRenderWindow.newAPISpecificView('WebGL');
        this.mainRenderWindow.addView(this.mainView);
        this.mainView.initialize();
    }

    createViewport(
        id: string,
        container: HTMLElement,
        type: ViewportType,
        options?: AnyViewportOptions
    ): IViewport {
        if (this.viewports.has(id)) {
            return this.viewports.get(id)!;
        }

        // 1. Create child render window and register with main
        const childRenderWindow = vtkRenderWindow.newInstance();
        this.mainRenderWindow.addRenderWindow(childRenderWindow);

        // 2. Create child view sharing the WebGL context
        const childView = this.mainView.addMissingNode(childRenderWindow);
        childRenderWindow.addView(childView);

        // 3. Bind to container and size (apply DPR for high-density displays)
        childView.setContainer(container);
        const { width, height } = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        childView.setSize(Math.max(Math.floor(width * dpr), 1), Math.max(Math.floor(height * dpr), 1));

        // VTK.js only sets canvas.style.width='100%' — without an explicit height,
        // WebKit derives the CSS height from the raw buffer dimensions instead of the
        // container, causing a DPR-proportional mismatch on high-density displays.
        const vtkCanvas = childView.getCanvas();
        if (vtkCanvas) {
            vtkCanvas.style.height = '100%';
        }

        // 4. Set up interactor (required for VTK rendering pipeline, but we do NOT
        //    bind DOM events — all pointer/wheel/keyboard events are handled by
        //    InteractionService and routed through the ToolGroup system instead).
        const interactor = vtkRenderWindowInteractor.newInstance();
        interactor.setView(childView);
        interactor.initialize();

        // 5. Create VTK context for viewport
        const vtkContext: ViewportVtkContext = {
            childRenderWindow,
            childView,
            interactor,
            container,
            render: () => this.requestRender(id),
        };

        // 6. Create the appropriate viewport subclass
        const viewport = this.createViewportInstance(id, type, vtkContext, options);
        this.viewports.set(id, viewport);

        // 7. Sync main view with all child render windows
        this.mainView.resizeFromChildRenderWindows();

        this.broadcast(this.EVENTS.VIEWPORT_CREATED, { id, type });

        return viewport;
    }

    private createViewportInstance(
        id: string,
        type: ViewportType,
        vtkContext: ViewportVtkContext,
        options?: AnyViewportOptions
    ): IViewport {
        switch (type) {
            // Each constructor narrows the option set itself; members it doesn't
            // declare are simply ignored.
            case ViewportType.VOLUME_3D:
                return new VolumeViewport3D(id, type, vtkContext, this.context, options);

            case ViewportType.SLICE_AXIAL:
            case ViewportType.SLICE_SAGITTAL:
            case ViewportType.SLICE_CORONAL:
                return new OrthogonalSliceViewport2D(id, type, vtkContext, this.context, options);

            case ViewportType.SLICE_OBLIQUE:
                return new ObliqueSliceViewport2D(id, type, vtkContext, this.context, options);

            case ViewportType.CPR_STRETCHED:
            case ViewportType.CPR_STRAIGHTENED:
                return new CprViewport2D(id, type, vtkContext, this.context, options);

            default:
                throw new Error(`Unsupported viewport type: ${type}`);
        }
    }

    destroyViewport(id: string): void {
        const viewport = this.viewports.get(id);
        if (viewport) {
            viewport.dispose();
            this.viewports.delete(id);
            this.mainView.resizeFromChildRenderWindows();
            this.broadcast(this.EVENTS.VIEWPORT_DESTROYED, { id });
        }
    }

    getViewport(id: string): IViewport | undefined {
        return this.viewports.get(id);
    }

    /**
     * Render all viewports (debounced)
     */
    renderAll(): void {
        // Mark all viewports dirty and schedule rendering
        for (const id of this.viewports.keys()) {
            this.dirtyViewports.add(id);
        }

        if (this.pendingRenderRequestHandle !== null) {
            return; // Already scheduled
        }

        this.pendingRenderRequestHandle = requestAnimationFrame(() => {
            this.pendingRenderRequestHandle = null;
            this.flushPendingRenders();
        });
    }

    /**
     * Force immediate render of all viewports.
     */
    forceRenderAll(): void {
        if (this.pendingRenderRequestHandle !== null) {
            cancelAnimationFrame(this.pendingRenderRequestHandle);
            this.pendingRenderRequestHandle = null;
        }
        this.dirtyViewports.clear();
        this.mainRenderWindow.render();
    }

    resizeViewport(id: string): void {
        const viewport = this.viewports.get(id);
        if (viewport) {
            viewport.resize();
            this.mainView.resizeFromChildRenderWindows();
        }
    }

    // ========================================================================
    // Facade methods
    // ========================================================================

    /**
     * Get the first viewport matching the given ViewportType. Returns undefined if none.
     */
    getViewportByType(type: ViewportType): IViewport | undefined {
        for (const vp of this.viewports.values()) {
            if (vp.type === type) return vp;
        }
        return undefined;
    }

    /**
     * Get slice information for a viewport. Returns null for non-slice viewports.
     */
    getSliceInfo(viewportId: string): SliceInfo | null {
        const viewport = this.viewports.get(viewportId);
        if (!(viewport instanceof SliceViewport2D)) return null;
        return {
            sliceIndex: viewport.sliceIndex,
            sliceCount: viewport.sliceCount,
            isOblique: viewport instanceof ObliqueSliceViewport2D,
        };
    }

    /**
     * Set slice index on a slice viewport. No-op for non-slice viewports.
     */
    setSliceIndex(viewportId: string, index: number): void {
        const viewport = this.viewports.get(viewportId);
        if (viewport instanceof SliceViewport2D) {
            viewport.setSliceIndex(index);
        }
    }

    /** Set the square field of view (mm) on an oblique 2D slice viewport. No-op otherwise. */
    setObliqueFieldOfViewMm(viewportId: string, fovMm: number): void {
        const viewport = this.viewports.get(viewportId);
        if (viewport instanceof ObliqueSliceViewport2D) {
            viewport.setFieldOfViewMm(fovMm);
        }
    }

    /** Reset the camera of a 3D volume viewport to its default framing. No-op otherwise. */
    resetCamera(viewportId: string): void {
        const viewport = this.viewports.get(viewportId);
        if (viewport instanceof VolumeViewport3D) {
            // Broadcast before resetting: listeners may restore camera state
            // (e.g. cancelling a C-arm draft) that must not overwrite the
            // default framing applied below
            this.broadcast(this.EVENTS.CAMERA_RESET, { viewportId });
            viewport.resetCameraToDefault();
        }
    }

    /**
     * Create a standalone viewport with full interaction and tool wiring.
     * Use this for viewports outside the main layout grid (e.g. report pages).
     */
    initStandaloneViewport(viewportId: string, options: StandaloneViewportOptions): StandaloneViewportHandle {
        const { viewportType, container, trackResize = true, ...viewportOptions } = options;

        const interactionService = this.context.services.getService<InteractionService>(
            InteractionService.SERVICE_NAME
        );
        const toolGroupManager = this.context.services.getService<ToolGroupManager>(
            ToolGroupManager.SERVICE_NAME
        );

        const viewport = this.createViewport(viewportId, container, viewportType, viewportOptions);
        const removeInteractionProxy = interactionService.initDomInteractionProxy(container, viewport);
        toolGroupManager.createDefaultToolGroup(viewportType, viewport);
        this.resizeViewport(viewportId);

        let observer: ResizeObserver | undefined;
        if (trackResize) {
            observer = new ResizeObserver(() => {
                this.resizeViewport(viewportId);
                viewport.render();
            });
            observer.observe(container);
        }

        return {
            viewport,
            dispose: () => {
                observer?.disconnect();
                removeInteractionProxy();
                toolGroupManager.removeViewportFromToolGroup(viewportId);
                this.destroyViewport(viewportId);
            },
        };
    }

    /**
     * Force all display objects on a viewport to re-run their `update()` cycle.
     *
     * Needed after `initStandaloneViewport()` for 2D slice viewports because
     * the base-class constructor creates display objects before subclass fields
     * (e.g. `slicePlane`) are assigned. Calling this once the viewport is fully
     * constructed gives display objects a second chance to build their pipelines.
     */
    refreshDisplayObjects(viewportId: string): void {
        this.viewports.get(viewportId)?.refreshDisplayObjects();
    }

    /**
     * Capture a viewport snapshot composited with its overlay layer.
     *
     * Delegates to {@link BaseViewport.captureImage} which internally composites
     * the VTK render with the 2D overlay canvas in a single pass.
     */
    async captureViewportSnapshot(
        viewportId: string,
        options?: SnapshotOptions & { captureOverlay?: boolean }
    ): Promise<string | null> {
        const viewport = this.viewports.get(viewportId);
        if (!viewport) return null;

        return viewport.captureImage(options?.mimeType ?? 'image/png', options?.captureOverlay);
    }

    override reset(): void {
        if (this.pendingRenderRequestHandle !== null) {
            cancelAnimationFrame(this.pendingRenderRequestHandle);
            this.pendingRenderRequestHandle = null;
        }
        this.dirtyViewports.clear();

        this.viewports.forEach((vp) => vp.dispose());
        this.viewports.clear();
        super.reset();
    }

    private requestRender(id: string): void {
        this.dirtyViewports.add(id);

        if (this.pendingRenderRequestHandle !== null) {
            return; // Already scheduled
        }

        this.pendingRenderRequestHandle = requestAnimationFrame(() => {
            this.pendingRenderRequestHandle = null;
            this.flushPendingRenders();
        });
    }

    private flushPendingRenders(): void {
        const dirtyViewports = new Set<string>(this.dirtyViewports);
        this.dirtyViewports.clear();

        for (const viewportId of dirtyViewports) {
            const viewport = this.viewports.get(viewportId);
            if (viewport) {
                viewport.getRenderWindow().render();
            }
        }
    }
}
